
import React from "react";
import { useParams, Navigate } from "react-router-dom";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import SEO from "@/components/SEO";
import StructuredData from "@/components/StructuredData";
import Breadcrumb from "@/components/Breadcrumb";
import { getProfessionData, professionExists } from "@/data/professionSolutions";
import ProfessionHero from "@/components/professions/ProfessionHero";
import RelatedProfessions from "@/components/professions/RelatedProfessions";
import ContactCTA from "@/components/professions/ContactCTA";
import MetierGuarantees from "@/components/metiers/MetierGuarantees";
import MetierSolutions from "@/components/metiers/MetierSolutions";
import MetierProcess from "@/components/metiers/MetierProcess";
import MetierAdvantages from "@/components/metiers/MetierAdvantages";
import { Users } from "lucide-react";

const MetierDetail = () => {
  console.log("MetierDetail component loaded");
  
  // Récupération du slug depuis l'URL - d'abord essayer useParams, sinon extraire de l'URL
  const { slug: paramSlug } = useParams<{ slug: string }>();
  const location = window.location.pathname;
  
  // Extraire le slug depuis l'URL si useParams ne le trouve pas
  const extractedSlug = paramSlug || location.split('/').pop();
  
  console.log("MetierDetail location:", location);
  console.log("MetierDetail paramSlug:", paramSlug);
  console.log("MetierDetail extractedSlug:", extractedSlug);
  
  // Vérification si le métier existe
  if (!extractedSlug || !professionExists(extractedSlug)) {
    console.log("Profession not found for slug:", extractedSlug);
    return <Navigate to="/services/professionnels" replace />;
  }
  
  // Récupération des données du métier
  const profession = getProfessionData(extractedSlug);
  
  // Si le métier n'existe pas (par sécurité)
  if (!profession) {
    return <Navigate to="/services/professionnels" replace />;
  }

  // Schema.org pour le métier
  const professionSchema = {
    "@context": "https://schema.org",
    "@type": "Service",
    "name": profession.title,
    "description": profession.description,
    "provider": {
      "@type": "LocalBusiness",
      "name": "Groupe Fabre Environnement",
      "url": "https://www.groupefabre-environnement.fr",
      "telephone": "+33565787900",
      "email": "contactgfe@groupefabre.com"
    },
    "serviceType": "Gestion des déchets professionnels",
    "category": "Recyclage et valorisation",
    "audience": {
      "@type": "Audience",
      "audienceType": profession.title.toLowerCase()
    }
  };

  return (
    <div className="flex flex-col min-h-screen bg-gradient-to-br from-gray-50 to-white">
      <SEO 
        title={`${profession.title} - Solutions de recyclage professionnel | Groupe Fabre`}
        description={`${profession.description} Services de gestion des déchets, collecte, tri et valorisation pour les professionnels du secteur. Devis gratuit et solutions sur-mesure.`}
        canonical={`/nos-metiers/${extractedSlug}`}
        keywords={`recyclage ${extractedSlug}, gestion déchets ${extractedSlug}, valorisation ${extractedSlug}, collecte déchets, tri sélectif professionnel`}
      />
      
      <StructuredData data={professionSchema} />
      
      <Navbar />
      
      <main className="flex-grow pt-20">
        {/* Hero Section */}
        <ProfessionHero profession={profession} />
        
        {/* Breadcrumb */}
        <nav aria-label="Fil d'Ariane" className="bg-white py-4 border-b border-gray-100 shadow-sm">
          <div className="container mx-auto px-4">
            <Breadcrumb
              items={[
                { label: "Métiers", href: "/nos-metiers" },
                { label: profession.title.replace("Solutions de recyclage pour ", ""), href: `/nos-metiers/${extractedSlug}`, current: true }
              ]}
            />
          </div>
        </nav>

        <article className="py-16">
          <div className="container mx-auto px-4">
            <div className="max-w-6xl mx-auto">
              <MetierGuarantees profession={profession} />
              <MetierSolutions profession={profession} />
              <MetierProcess />
              <MetierAdvantages />
              
              {/* Call to Action */}
              <section className="mb-16">
                <ContactCTA />
              </section>

              {/* Métiers associés */}
              {profession.relatedProfessions.length > 0 && (
                <section>
                  <div className="bg-white rounded-3xl shadow-xl p-8 border border-gray-100">
                    <h2 className="text-2xl font-heading font-bold mb-6 text-fabre-gray flex items-center justify-center">
                      <Users className="h-6 w-6 mr-3" />
                      Métiers associés
                    </h2>
                    <div className="w-24 h-1 bg-gradient-to-r from-fabre-green to-fabre-teal mx-auto mb-8 rounded-full"></div>
                    <RelatedProfessions relatedProfessions={profession.relatedProfessions} />
                  </div>
                </section>
              )}
            </div>
          </div>
        </article>
      </main>
      
      <Footer />
    </div>
  );
};

export default MetierDetail;
